import { useEffect, useRef } from 'react'

export function generateSlug(title: string): string {
    if (!title) return ''

    const slug = title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')

    if (slug.length <= 15) {
        return slug
    }

    const words = slug.split('-')

    if (words.length === 1) {
        return slug.substring(0, 15)
    }

    const commonWords = [
        'a',
        'an',
        'and',
        'as',
        'at',
        'be',
        'by',
        'for',
        'from',
        'in',
        'is',
        'it',
        'of',
        'on',
        'or',
        'the',
        'to',
        'up',
        'we',
    ]
    const filteredWords = words.filter(word => !commonWords.includes(word))

    const wordsToUse = filteredWords.length > 0 ? filteredWords : words

    let result = wordsToUse[0] || ''

    for (let i = 1; i < wordsToUse.length; i++) {
        const nextWord = wordsToUse[i]
        if (result.length + 1 + nextWord.length <= 15) {
            result += `-${nextWord}`
        } else {
            const remainingSpace = 15 - result.length - 1
            if (remainingSpace > 2) {
                result += `-${nextWord.substring(0, remainingSpace)}`
            }
            break
        }
    }

    return result
}

interface UseSlugOptions<T extends Record<string, unknown>> {
    watch: (name: keyof T) => T[keyof T]
    setValue: (name: keyof T, value: T[keyof T]) => void
}

export function useSlug<T extends Record<string, unknown>>(
    { watch, setValue }: UseSlugOptions<T>,
    titleField: keyof T = 'name' as keyof T,
    slugField: keyof T = 'identifier' as keyof T
): void {
    const wasManuallyModifiedRef = useRef<boolean>(false)
    const lastTitleRef = useRef<string>('')
    const lastSlugRef = useRef<string>('')

    const title = watch(titleField) as string
    const slug = watch(slugField) as string

    useEffect(() => {
        const generatedSlug = generateSlug(title || '')

        if (!slug?.trim()) {
            wasManuallyModifiedRef.current = false
            lastTitleRef.current = title || ''
            lastSlugRef.current = generatedSlug
            setValue(slugField, generatedSlug as T[keyof T])
            return
        }

        if (lastSlugRef.current !== slug && slug !== generatedSlug) {
            wasManuallyModifiedRef.current = true
        }

        if (lastTitleRef.current !== title && !wasManuallyModifiedRef.current) {
            lastTitleRef.current = title || ''
            lastSlugRef.current = generatedSlug
            setValue(slugField, generatedSlug as T[keyof T])
            return
        }

        lastTitleRef.current = title || ''
        lastSlugRef.current = slug || ''
    }, [title, slug, setValue, slugField])
}
